import { useEffect } from 'react';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { useGetEmployee } from 'src/components/enterprise/features/Employee/EditPage/hooks/useGetEmployee';
import { useFetchDepartment } from 'src/components/common/hooks/useFetchDepartment/useFetchDepartment';
import { useFetchLocation } from 'src/components/common/hooks/useFetchLocation/useFetchLocation';
import {
  extractEmployeeInfo,
  EmployeeInfo,
} from 'src/components/enterprise/features/Employee/EmployeeDetailsPage/components/EmployeeDetailsWrapper/EmployeeDetailsInfo/functions/employeeDetailsInfo.utils';

export type EmployeeDetailsInfoReturn = {
  isLoading: boolean;
  employeeInfo: EmployeeInfo | null;
};

export type UseEmployeeDetailsInfoProps = {
  corpId: string;
  employeeId: string;
};

export const useEmployeeDetailsInfo = ({
  corpId,
  employeeId,
}: UseEmployeeDetailsInfoProps): EmployeeDetailsInfoReturn => {
  const { corpData } = useAuth();

  const { isLoading: isLoadingEmployee, employeeData } = useGetEmployee(
    corpId,
    employeeId
  );

  const {
    isLoading: isLoadingDepartment,
    department,
    fetchDepartment,
  } = useFetchDepartment(corpId);

  const {
    isLoading: isLoadingLocation,
    location,
    fetchLocation,
  } = useFetchLocation(corpId);

  useEffect(() => {
    if (corpId && employeeData?.departmentId) {
      fetchDepartment(employeeData.departmentId);
    }
  }, [corpId, employeeData?.departmentId, fetchDepartment]);

  useEffect(() => {
    if (corpId && employeeData?.locationId) {
      fetchLocation(employeeData.locationId);
    }
  }, [corpId, employeeData?.locationId, fetchLocation]);

  const isLoading =
    isLoadingEmployee || isLoadingDepartment || isLoadingLocation;

  return {
    isLoading,
    employeeInfo: employeeData
      ? extractEmployeeInfo(
          employeeData,
          corpData,
          department?.name,
          location?.name
        )
      : null,
  };
};
